import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  X, Trophy, MessageSquare, Calendar, Users, 
  Lightbulb, ChevronRight, Clock
} from 'lucide-react';

// Captain avatar states
const CAPTAIN_AVATAR = '/captain-happy.png';

const WelcomeBackRecap = ({ onDismiss }) => {
  const { user, api } = useAuth();
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(true);
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    fetchRecap();
  }, []);

  const fetchRecap = async () => {
    try {
      const response = await api.get('/users/me/welcome-recap');
      // Use localStorage last_login_time for display (more accurate for this use case)
      const localLastLogin = localStorage.getItem('last_login_time');
      setRecap({
        ...response.data,
        last_login: localLastLogin || response.data.last_login
      });
    } catch (error) {
      console.error('Error fetching recap:', error);
      // Mock data if endpoint doesn't exist
      setRecap({
        last_login: localStorage.getItem('last_login_time') || new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        new_messages: 12,
        new_victories: 3,
        new_events: 2,
        new_members: 5,
        new_ideas: 4,
        highlights: [
          { type: 'victory', user: 'Marie D.', text: 'a signé son premier compromis !' },
          { type: 'event', text: 'Live Q&A ce jeudi à 19h' },
          { type: 'member', user: 'Thomas L.', text: 'a rejoint la communauté' },
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    // Mark recap as seen and update last login time
    localStorage.setItem('last_recap_seen', new Date().toISOString());
    localStorage.setItem('last_login_time', new Date().toISOString());
    setTimeout(() => {
      if (onDismiss) onDismiss();
    }, 300);
  };

  const formatTimeSince = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "aujourd'hui";
    if (diffDays === 1) return "hier";
    if (diffDays < 7) return `il y a ${diffDays} jours`;
    if (diffDays < 30) return `il y a ${Math.floor(diffDays / 7)} semaine${diffDays >= 14 ? 's' : ''}`;
    return `il y a ${Math.floor(diffDays / 30)} mois`;
  };

  const getHighlightIcon = (type) => {
    switch (type) {
      case 'victory': return <Trophy className="w-4 h-4 text-yellow-500" />;
      case 'event': return <Calendar className="w-4 h-4 text-red-500" />;
      case 'member': return <Users className="w-4 h-4 text-blue-500" />;
      case 'idea': return <Lightbulb className="w-4 h-4 text-primary" />;
      default: return <MessageSquare className="w-4 h-4 text-muted-foreground" />;
    }
  };

  if (loading || !recap) return null;

  // Check if there's anything to show - always show welcome if it's been a while
  const hasContent = recap.new_messages > 0 || recap.new_victories > 0 || 
    recap.new_events > 0 || recap.new_members > 0 || recap.new_ideas > 0;
  
  // If no content, show a simple welcome back message
  const showSimpleWelcome = !hasContent;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] pointer-events-none"
          data-testid="welcome-recap"
        >
          {/* Backdrop - clickable to dismiss */}
          <div 
            className="absolute inset-0 bg-black/40 pointer-events-auto"
            onClick={handleDismiss}
          />
          
          {/* Avatar and Speech Bubble Container - positioned at bottom left */}
          <div className="absolute bottom-20 left-4 flex items-end gap-3 pointer-events-auto">
            {/* Captain Avatar */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              className="relative flex-shrink-0"
            >
              {/* Glow effect */}
              <div className="absolute -inset-2 bg-primary/30 rounded-full blur-xl animate-pulse" />
              
              {/* Avatar container */}
              <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-cyan-500/20 border-2 border-primary/40 p-1 shadow-xl">
                {avatarError ? (
                  <div className="w-full h-full rounded-full bg-primary/20 flex items-center justify-center text-3xl" aria-label="Capitaine">
                    👋
                  </div>
                ) : (
                  <img
                    src={CAPTAIN_AVATAR}
                    alt="Capitaine"
                    className="w-full h-full object-contain drop-shadow-lg"
                    onError={() => setAvatarError(true)}
                  />
                )}
              </div>
            </motion.div>

            {/* Speech Bubble */}
            <motion.div
              initial={{ x: -20, opacity: 0, scale: 0.9 }}
              animate={{ x: 0, opacity: 1, scale: 1 }}
              exit={{ x: -20, opacity: 0, scale: 0.9 }}
              transition={{ delay: 0.1, type: 'spring', damping: 20 }}
              className="relative max-w-sm"
            >
              {/* Bubble pointer */}
              <div className="absolute left-0 bottom-8 -ml-2 w-4 h-4 bg-card rotate-45 border-l border-b border-border" />
              
              {/* Bubble content */}
              <div className="relative bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
                {/* Close button */}
                <button
                  onClick={handleDismiss}
                  className="absolute top-2 right-2 p-1 rounded-lg hover:bg-secondary transition-colors z-10"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>

                {/* Header */}
                <div className="px-4 pt-4 pb-2">
                  <h3 className="font-bold text-lg pr-6">
                    Bon retour, {user?.first_name} ! 👋
                  </h3>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" />
                    Dernière visite {formatTimeSince(recap.last_login)}
                  </p>
                </div>

                {/* Stats */}
                <div className="px-4 py-3">
                  {showSimpleWelcome ? (
                    <p className="text-sm text-muted-foreground">
                      Tout est calme sur le pont ! Rien de nouveau pendant ton absence, mais l'équipage est content de te revoir. 🌊
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground mb-2">
                        Voici ce qui s'est passé :
                      </p>
                      
                      <div className="flex flex-wrap gap-2">
                        {recap.new_messages > 0 && (
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-xs">
                            <MessageSquare className="w-3 h-3 text-blue-500" />
                            <span className="font-medium">{recap.new_messages}</span>
                            <span className="text-muted-foreground">messages</span>
                          </div>
                        )}
                        
                        {recap.new_victories > 0 && (
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-xs">
                            <Trophy className="w-3 h-3 text-yellow-500" />
                            <span className="font-medium">{recap.new_victories}</span>
                            <span className="text-muted-foreground">victoires</span>
                          </div>
                        )}
                        
                        {recap.new_events > 0 && (
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-xs">
                            <Calendar className="w-3 h-3 text-red-500" />
                            <span className="font-medium">{recap.new_events}</span>
                            <span className="text-muted-foreground">événements</span>
                          </div>
                        )}
                        
                        {recap.new_members > 0 && (
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-xs">
                            <Users className="w-3 h-3 text-green-500" />
                            <span className="font-medium">{recap.new_members}</span>
                            <span className="text-muted-foreground">nouveaux</span>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Highlights - only show if there's content */}
                {!showSimpleWelcome && recap.highlights && recap.highlights.length > 0 && (
                  <div className="px-4 py-2 border-t border-border bg-secondary/30">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Temps forts</p>
                    <div className="space-y-1.5">
                      {recap.highlights.slice(0, 2).map((highlight, index) => (
                        <div 
                          key={`${highlight.type}-${highlight.user || ''}-${index}`}
                          className="flex items-center gap-2 text-xs"
                        >
                          {getHighlightIcon(highlight.type)}
                          <p className="truncate">
                            {highlight.user && <span className="font-medium">{highlight.user} </span>}
                            {highlight.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div className="px-4 py-3 flex gap-2 border-t border-border">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={handleDismiss}
                  >
                    Passer
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={handleDismiss}
                  >
                    Explorer
                    <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WelcomeBackRecap;
